import { privateApi } from '@/config/axios.config';

const API_BASE = '/condominium/administrators';

export interface CondominiumAdministrator {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: string;
  status: 'active' | 'inactive' | 'suspended' | 'pending_verification';
  avatar?: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateAdministratorDTO {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  password: string;
  role: string; // Será CONDOMINIUM_ADMIN
}

export interface UpdateAdministratorDTO {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: string;
  status?: 'active' | 'inactive' | 'suspended' | 'pending_verification';
  password?: string;
}

export const administratorsApiService = {
  // Obtener todos los administradores del condominio
  async getAdministrators(): Promise<CondominiumAdministrator[]> {
    try {
      const response = await privateApi.get(API_BASE);
      
      // El backend devuelve: { success: true, data: { users: [...], total: number, ... } }
      // O en algunos casos: { success: true, data: { data: { users: [...], ... } } }
      const responseData = response.data;
      
      // Intentar múltiples estructuras posibles
      let users: any[] = [];
      
      // Estructura 1: response.data.data.users (doble data)
      if (responseData?.data?.data?.users && Array.isArray(responseData.data.data.users)) {
        users = responseData.data.data.users;
      }
      // Estructura 2: response.data.data.users (simple, pero data es el objeto con users)
      else if (responseData?.data?.users && Array.isArray(responseData.data.users)) {
        users = responseData.data.users;
      }
      // Estructura 3: response.data.users (directo)
      else if (responseData?.users && Array.isArray(responseData.users)) {
        users = responseData.users;
      }
      // Estructura 4: response.data es directamente un array
      else if (Array.isArray(responseData)) {
        users = responseData;
      }
      // Estructura 5: response.data.data es directamente un array
      else if (Array.isArray(responseData?.data)) {
        users = responseData.data;
      }
      else {
        users = [];
      }
      
      if (!Array.isArray(users) || users.length === 0) {
        return [];
      }
      
      return users.map((user: any) => ({
        id: user.id,
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        role: user.role || 'CONDOMINIUM_ADMIN',
        status: user.status || 'active',
        avatar: user.avatar || '',
        lastLoginAt: user.lastLoginAt,
        createdAt: user.createdAt || new Date().toISOString(),
        updatedAt: user.updatedAt,
      }));
    } catch (error: any) {
      throw error;
    }
  },

  // Obtener un administrador por ID
  async getAdministratorById(id: string): Promise<CondominiumAdministrator> {
    const response = await privateApi.get(`${API_BASE}/${id}`);
    
    const backendData = response.data.data || response.data;
    
    return {
      id: backendData.id,
      email: backendData.email,
      firstName: backendData.firstName,
      lastName: backendData.lastName,
      phone: backendData.phone || '',
      role: backendData.role,
      status: backendData.status,
      avatar: backendData.avatar || '',
      lastLoginAt: backendData.lastLoginAt,
      createdAt: backendData.createdAt,
      updatedAt: backendData.updatedAt,
    };
  },

  // Crear un nuevo administrador
  async createAdministrator(data: CreateAdministratorDTO): Promise<CondominiumAdministrator> {
    const response = await privateApi.post(API_BASE, {
      ...data,
      role: 'CONDOMINIUM_ADMIN', // Forzar el rol
    });
    
    const backendData = response.data.data || response.data;
    
    return {
      id: backendData.id,
      email: backendData.email,
      firstName: backendData.firstName,
      lastName: backendData.lastName,
      phone: backendData.phone || '',
      role: backendData.role,
      status: backendData.status,
      avatar: backendData.avatar || '',
      lastLoginAt: backendData.lastLoginAt,
      createdAt: backendData.createdAt,
      updatedAt: backendData.updatedAt,
    };
  },

  // Actualizar un administrador
  async updateAdministrator(
    id: string,
    data: UpdateAdministratorDTO
  ): Promise<CondominiumAdministrator> {
    const response = await privateApi.patch(`${API_BASE}/${id}`, data);
    
    const backendData = response.data.data || response.data;
    
    return {
      id: backendData.id,
      email: backendData.email,
      firstName: backendData.firstName,
      lastName: backendData.lastName,
      phone: backendData.phone || '',
      role: backendData.role,
      status: backendData.status,
      avatar: backendData.avatar || '',
      lastLoginAt: backendData.lastLoginAt,
      createdAt: backendData.createdAt,
      updatedAt: backendData.updatedAt,
    };
  },

  // Eliminar un administrador
  async deleteAdministrator(id: string): Promise<void> {
    await privateApi.delete(`${API_BASE}/${id}`);
  },
};

